import {
  DivisionEntity,
  CompanyEntity,
  UserEntity,
  DivisionTranslationEntity,
  DivisionTypeEnum,
  FontDefinitionDto,
  DivisionBehaviour,
  LanguageEntity,
  DocumentDefinitionDto,
  MarkdownTextDto,
} from "@citychallenge/api-public";
import { observable, action, computed } from "mobx";
import { GameStore } from "./game-store";
import { ArticleStore } from "./article-store";
import { EventStore } from "./event.store";
import { Loadable } from "../loadable";
import { Environment } from "../environment";
import { Types } from "@citychallenge/common";

export interface DivisionData extends DivisionEntity {
  authError?: boolean;
}

export class DivisionModel implements DivisionEntity {
  public id!: string;

  public type = DivisionTypeEnum.Division;

  @observable
  public name!: string;

  @observable
  public domains?: string[];

  public created?: string;
  public updated?: string;

  @observable
  public company?: CompanyEntity;

  @observable
  public code!: string;

  @observable
  public color?: string;

  @observable
  public colorBackground?: string;

  @observable
  public colorText?: string;

  @observable
  public fontPrimary?: FontDefinitionDto | undefined;

  @observable
  public fontSecondary?: FontDefinitionDto | undefined;

  @observable
  public icon?: string;

  @observable
  public backgroundImage?: string;

  @observable
  public description?: Types["MarkdownText"];

  @observable
  public contact?: UserEntity;

  @observable
  public contactButtonTitle?: string;

  @observable
  public contactPageText?: MarkdownTextDto;

  @observable
  public contactPageImage?: string;

  public baseLanguage?: LanguageEntity;

  @observable
  public translations?: Array<DivisionTranslationEntity>;

  public hasContact = false;

  @computed
  public get hasContactPage() {
    if (this.contactPageText?.text) return true;

    if (this.hasContact) return true;

    return false;
  }

  @computed
  public get supportedLanguages() {
    const languages: string[] = [];

    if (this.baseLanguage) languages.push(this.baseLanguage.id);

    if (this.translations)
      for (const { language } of this.translations) {
        if (language) languages.push(language.id);
      }

    return languages;
  }

  public forceAuthentication?: boolean;

  @observable
  public experiencesTabTitle?: string;

  @observable
  public experiencesWelcomeText?: string;

  @observable
  public articlesEnabled?: boolean;

  @observable
  public articlesTabTitle?: string;

  @observable
  public eventsEnabled?: boolean;

  @observable
  public eventsTabTitle?: string;

  @observable
  public disableEventCreation?: boolean;

  @observable
  public invertEnergyPoints?: boolean;

  @observable
  public mapBased?: boolean;

  @observable
  public mapBasedDisableGps?: boolean;

  @observable
  public mapBasedTitle?: string;

  @observable
  public mapBasedShowLegend?: boolean;

  @observable
  public alwaysHideMap?: boolean;

  @observable
  public disableStoryOverview?: boolean;

  @observable
  public customMapUrl?: string;

  @observable
  public behaviour?: DivisionBehaviour;

  @observable
  public privacyDocument?: DocumentDefinitionDto;

  @observable
  public tosDocument?: DocumentDefinitionDto;

  @observable
  public cookiePolicyDocument?: DocumentDefinitionDto;

  @observable
  public customPolicyDocument?: DocumentDefinitionDto;

  @computed
  public get isLearning() {
    return (
      Environment.type === "learning" ||
      this.articlesEnabled ||
      this.eventsEnabled
    );
  }

  @computed
  public get environment() {
    return Environment.type;
  }

  @computed
  public get showArticlesTab() {
    return this.articlesEnabled;
  }

  @computed
  public get showEventsTab() {
    return this.eventsEnabled;
  }

  @computed
  public get showInlineMap() {
    return !this.alwaysHideMap;
  }

  constructor(data: DivisionEntity) {
    this.assignData(data);
  }

  @action
  public update(data: DivisionEntity) {
    this.assignData(data);
  }

  private assignData = (data: DivisionData) => {
    // FIX: Apparently these stores are exported to JSON definition, which should
    // not really happen because it overrides the way the stores are supposed
    // to work (with mobx handling the state changes)
    delete (data as any).gameStore;
    delete (data as any).articleStore;
    delete (data as any).eventStore;

    Object.assign(this, data);
  };

  @observable
  public authError = false;

  public loadableGameStore?: Loadable<GameStore>;
  public readonly gameStore = new GameStore([]);

  public loadableArticleStore?: Loadable<ArticleStore>;
  public readonly articleStore = new ArticleStore([]);

  public loadableEventStore?: Loadable<EventStore>;
  public readonly eventStore = new EventStore([]);
}
